import { AuthService } from '@/services/auth.service';
import $store from '../store';

/**
 * Current user state initialization
 * @WARN Must always be the first in the middleware chain
 */
export async function initCurrentUserStateMiddleware(to, from, next) {
  const currentUserId = $store?.state?.user?.currentUser?.id;

  if (AuthService.hasToken() && !currentUserId) {
    try {
      await AuthService.getProfile();
      next();
    } catch (error) {
      AuthService.removeToken();
      next();
    }
  } else {
    next();
  }
}

/**
 * Middleware to check access permissions for authenticated routes
 */
export function checkAccessMiddleware(to, from, next) {
  const currentUserId = $store?.state?.user?.currentUser?.id;
  const isAuthRoute = to.matched.some((route) => route.meta.isAuth);
  const authNotRequired = ['KioskLogin', 'KioskCreateAccount'].includes(to.name);

  if (currentUserId && authNotRequired) {
    // Redirect logged-in users away from login, register, or welcome pages
    const userRole = $store?.state?.user?.currentUser?.objectType;
    if (["superadmin", "super_admin", "super-admin"].includes(userRole)) {
      return next({ name: "SuperAdminSelection" });
    } else {
      return next({ name: "HomePage" });
    }
  }

  if (isAuthRoute && currentUserId) {
    // Allow access to authenticated routes if the user is logged in
    return next();
  }

  if (isAuthRoute) {
    // Redirect unauthenticated users to the login page
    return next({ name: 'KioskLogin' });
  }

  // Allow access to other routes
  next();
}

/**
 * Middleware to set the page title dynamically
 */
export function setPageTitleMiddleware(to, from, next) {
  const pageTitleRoute = to.matched.find((route) => route.meta.title);

  if (pageTitleRoute) {
    window.document.title = pageTitleRoute.meta.title;
  }

  next();
}
export function checkAuthorization(to, from, next) {
  const user = $store.state.user.currentUser;
  const userRole = user.objectType || null; // Get user role

  if (to.meta.isAuth) {
    if (to.meta.role && to.meta?.role !== userRole) {
      // Allow super-admins to access hospital-role routes
      if (
        ["superadmin", "super_admin", "super-admin", "admin", "hospital"].includes(userRole) &&
        to.meta.role === "hospital"
      ) {
        return next();
      }
      return next({ path: "/unauthorized" }); // ✅ Redirect to Unauthorized Page
    }
  }

  next();
}

/**
 * Middleware for User Management route.
 * - super_admin : full CRUD access
 * - admin       : read-only (view only)
 * - others      : redirect to /unauthorized
 */
export function checkUserManagementAccess(to, from, next) {
  // Only apply to routes that opt-in via meta.userManagementGuard
  if (!to.meta.userManagementGuard) return next();

  const user = $store.state.user.currentUser;
  const userRole = user.objectType || null;

  if (userRole === 'super_admin' || userRole === 'superadmin' || userRole === 'super-admin' || userRole === 'admin' || userRole === 'hospital') {
    // Full access – no restrictions
    return next();
  }

  if (userRole === 'admin') {
    // Read-only access – pass a flag so the component can hide action buttons
    return next();
  }

  // All other roles are not allowed
  return next({ path: '/unauthorized' });
}